import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col } from "reactstrap";
import { toast } from "sonner";
import { useCreateWorkLogMutation } from "store/features/client/clientManagementApi";
import { useSelector } from "react-redux";
import { selectUser } from "store/features/auth/authSlice";

const ProjectWorkLogModal = ({
  isOpen,
  toggle,
  project,
  onWorkLogAdded
}) => {
  const user = useSelector(selectUser);
  const [createWorkLog, { isLoading }] = useCreateWorkLogMutation();

  const [formData, setFormData] = useState({
    title: "",
    work_summary: "",
    hours_spent: "",
    work_status: "In Progress",
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        title: "",
        work_summary: "",
        hours_spent: "",
        work_status: "In Progress",
        date: new Date().toISOString().split('T')[0]
      });
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.work_summary.trim()) {
      toast.error("Work summary is required");
      return;
    }

    if (!formData.hours_spent || parseFloat(formData.hours_spent) <= 0) {
      toast.error("Please enter valid hours spent");
      return;
    }

    try {
      const workLogData = {
        ...formData,
        project_id: project?.id,
        employer_id: user.id,
        employer_name: user.nickname || user.name,
        employer_role: user.role,
        hours_spent: parseFloat(formData.hours_spent) || 0
      };

      await createWorkLog(workLogData).unwrap();
      toast.success("Work log added successfully!");

      // Reset form
      setFormData({
        title: "",
        work_summary: "",
        hours_spent: "",
        work_status: "In Progress",
        date: new Date().toISOString().split('T')[0]
      });

      // Close modal and callback
      toggle();
      if (onWorkLogAdded) {
        onWorkLogAdded();
      }
    } catch (error) {
      toast.error("Failed to add work log");
      console.error('Work log creation error:', error);
    }
  };

  const handleModalClose = () => {
    setFormData({
      title: "",
      work_summary: "",
      hours_spent: "",
      work_status: "In Progress",
      date: new Date().toISOString().split('T')[0]
    });
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleModalClose} size="lg">
      <ModalHeader toggle={handleModalClose}>
        Add Work Log - {project?.name}
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          {/* Project Information */}
          <div className="mb-4 p-3 bg-light rounded">
            <h6 className="text-primary mb-2">Project Information</h6>
            <div className="row">
              <div className="col-md-6">
                <small><strong>Project:</strong> {project?.name}</small>
              </div>
              <div className="col-md-6">
                <small><strong>Employee:</strong> {user.nickname || user.name}</small>
              </div>
            </div>
          </div>

          <Row>
            <Col lg={6}>
              <div className="mb-3">
                <label className="form-label">Date *</label>
                <input
                  type="date"
                  className="form-control"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <label className="form-label">Hours Spent *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="24"
                  className="form-control"
                  name="hours_spent"
                  value={formData.hours_spent}
                  onChange={handleChange}
                  placeholder="e.g., 7.5"
                  required
                />
                <small className="text-muted">Enter hours spent (0.5 for 30 minutes)</small>
              </div>
            </Col>
            {/* <Col lg={12}>
              <div className="mb-3">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter work title (optional)"
                />
              </div>
            </Col> */}
            <Col lg={12}>
              <div className="mb-3">
                <label className="form-label">Work Status *</label>
                <select
                  className="form-select"
                  name="work_status"
                  value={formData.work_status}
                  onChange={handleChange}
                  required
                >
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
            </Col>
            <Col lg={12}>
              <div className="mb-3">
                <label className="form-label">Work Summary *</label>
                <textarea
                  className="form-control"
                  name="work_summary"
                  value={formData.work_summary}
                  onChange={handleChange}
                  rows="6"
                  placeholder={`Describe what you worked on for ${project?.name} today. Be specific about tasks completed, progress made, and any challenges faced...`}
                  required
                />
                <small className="text-muted">
                  Please provide detailed information about your work on this project
                </small>
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="light" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={isLoading}>
            {isLoading ? "Adding..." : "Add Work Log"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default ProjectWorkLogModal;